import { useState } from "react";
import * as React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChecklistIcon from "@mui/icons-material/Checklist";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { PopoverProps } from "@mui/material/Popover";
import Stack from "@mui/material/Stack";

import { colord } from "colord";

import { useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import {
  TASK_STATUS_IN_PROGRESS,
  TASK_STATUS_TODO
} from "constants/staff-tasks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { usePatientNotesDrawer } from "features/patients/patient-notes-drawer";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import usePersistedState from "hooks/use-persisted-state";
import { useQueryStaffTasksCounterQuery } from "services/staff-tasks";

export default function TasksButton() {
  const { handleOpenTaskDrawer, handleOpenTaskListModal } = useStaffTasksV2();
  const { handleClose: handleCloseNotes } = usePatientNotesDrawer();
  const currentUser = useAppSelector(selectCurrentUser);

  const { data: tasksAssignedToMeCount = 0 } = useQueryStaffTasksCounterQuery(
    {
      assignee: currentUser?.id,
      is_active: true,
      is_complete: false,
      status: [TASK_STATUS_TODO, TASK_STATUS_IN_PROGRESS]
    },
    {
      skip: !currentUser?.id || currentUser?.id === -1
    }
  );

  const [anchorEl, setAnchorEl] = useState<PopoverProps["anchorEl"] | null>(
    null
  );
  const [selectedTaskType, setSelectedTaskType] = usePersistedState<string>(
    "taskButton/selectedTaskType",
    "My Tasks"
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openClinicTasks = () => {
    handleCloseNotes();
    handleOpenTaskListModal();
    handleMenuClose();
  };

  const openMyTasks = () => {
    handleCloseNotes();
    handleOpenTaskDrawer("view");
    handleMenuClose();
  };

  const isMyTasks = selectedTaskType === "My Tasks";

  return (
    <Stack>
      <Badge
        badgeContent={
          isMyTasks ? (
            <Box
              key={tasksAssignedToMeCount}
              className={
                tasksAssignedToMeCount
                  ? "animate__animated animate__flash"
                  : undefined
              }
            >
              {tasksAssignedToMeCount}
            </Box>
          ) : null
        }
        color="primary"
        sx={{
          zIndex: 0,
          "& .MuiBadge-anchorOriginTopRight": {
            visibility: isMyTasks ? undefined : "hidden",
            opacity: tasksAssignedToMeCount ? 1 : 0,
            transform: "translate(0px, -12px)"
          }
        }}
      >
        <Stack direction="row">
          {isMyTasks ? (
            <ChecklistIcon
              onClick={openMyTasks}
              htmlColor={colord(misc.darkGreen).darken(0.25).toHex()}
              sx={{ cursor: "pointer" }}
            />
          ) : (
            <LocalHospitalIcon
              onClick={openClinicTasks}
              htmlColor={colord(misc.darkGreen).darken(0.25).toHex()}
              sx={{ cursor: "pointer" }}
            />
          )}
          <Stack onClick={handleMenuOpen} sx={{ cursor: "pointer" }}>
            <ArrowDropDownIcon
              sx={{
                rotate: anchorEl ? "180deg" : "0deg",
                transition: "rotate 0.5s"
              }}
            />
          </Stack>
        </Stack>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          selected={isMyTasks}
          onClick={() => {
            setSelectedTaskType("My Tasks");
            openMyTasks();
          }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <ChecklistIcon
              fontSize="small"
              htmlColor={colord(misc.darkGreen).darken(0.25).toHex()}
            />
            My Tasks
          </Stack>
        </MenuItem>
        <MenuItem
          selected={!isMyTasks}
          onClick={() => {
            setSelectedTaskType("Clinic Tasks");
            openClinicTasks();
          }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <LocalHospitalIcon
              fontSize="small"
              htmlColor={colord(misc.darkGreen).darken(0.25).toHex()}
            />
            Clinic Tasks
          </Stack>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
